import apiClient from './api-client';

export interface ClientListItem {
    clientId: number;
    clientName: string;
    subdomain: string;
    isActive: boolean;
    signUpDate: string;
    commissioningDate: string | null;
    contactEmail: string | null;
    contactPhone: string | null;
    clientIndustry: string | null;
    packageId: number | null;
    recordCreateDate: string;
}

export interface ClientsDashboardSummary {
    totalClients: number;
    activeClients: number;
    inactiveClients: number;
    recentSignUpsCount: number;
}

interface ListClientsResponse {
    message: string;
    statusCode: number;
    data: ClientListItem[];
}

interface DashboardResponse {
    message: string;
    statusCode: number;
    data: ClientsDashboardSummary;
}

export async function listClients(): Promise<ClientListItem[]> {
    const response = await apiClient.get<ListClientsResponse>('/clients');
    return response?.data ?? [];
}

export async function getClientsDashboard(): Promise<ClientsDashboardSummary> {
    const response = await apiClient.get<DashboardResponse>('/clients/dashboard');
    return response?.data ?? {
        totalClients: 0,
        activeClients: 0,
        inactiveClients: 0,
        recentSignUpsCount: 0
    };
}

/** Single client detail (matches GET /clients/:clientId). */
export interface ClientDetail {
    clientId: number;
    clientName: string;
    subdomain: string;
    isActive: boolean;
    signUpDate: string;
    commissioningDate: string | null;
    contactEmail: string | null;
    contactPhone: string | null;
    clientIndustry: string | null;
    clientAddress: string | null;
    packageId: number | null;
    recordCreateDate: string;
}

interface ClientDetailApiResponse {
    message: string;
    statusCode: number;
    data: ClientDetail;
}

export async function getClientById(clientId: number): Promise<ClientDetail | null> {
    try {
        const response = await apiClient.get<ClientDetailApiResponse>(`/clients/${clientId}`);
        const body = response as ClientDetailApiResponse;
        return body?.data ?? (response as unknown as ClientDetail) ?? null;
    } catch {
        return null;
    }
}

/** Payload for PATCH /clients/:clientId (Phase 9+). Subdomain is not updatable. */
export interface UpdateClientPayload {
    isActive?: boolean;
    clientName?: string;
    contactEmail?: string | null;
    contactPhone?: string | null;
    clientAddress?: string | null;
    clientIndustry?: string | null;
    packageId?: number | null;
    /** ISO date string or null to clear. */
    commissioningDate?: string | null;
}

export async function updateClient(clientId: number, payload: UpdateClientPayload): Promise<ClientDetail | null> {
    try {
        const response = await apiClient.patch<ClientDetailApiResponse>(`/clients/${clientId}`, payload);
        const body = response as ClientDetailApiResponse;
        return body?.data ?? (response as unknown as ClientDetail) ?? null;
    } catch {
        return null;
    }
}

export interface CreateClientPayload {
    ClientName: string;
    Subdomain: string;
    ContactEmail?: string | null;
}

export interface CreateClientResult {
    clientId: number;
    subdomain: string;
    clientName: string;
}

interface CreateClientApiResponse {
    message: string;
    statusCode: number;
    data: CreateClientResult;
}

export async function createClient(payload: CreateClientPayload): Promise<CreateClientResult> {
    const response = await apiClient.post<CreateClientApiResponse>('/clients', {
        clientDetails: {
            ClientName: payload.ClientName,
            Subdomain: payload.Subdomain,
            ContactEmail: payload.ContactEmail ?? undefined,
        },
    });
    const body = response as CreateClientApiResponse;
    if (body?.data) return body.data;
    return response as unknown as CreateClientResult;
}

// --- Client roles (Roles tab) ---

export interface PermissionItem {
    permissionId: number;
    permissionName: string;
    description: string | null;
}

export interface ClientRoleItem {
    roleId: number;
    roleName: string;
    description: string | null;
    isSystemRole: boolean;
    permissionIds: number[];
}

export interface ClientRolesResponse {
    roles: ClientRoleItem[];
    allPermissions: PermissionItem[];
    /** True when this client can assign super-admin-only permissions (e.g. MANAGE_CLIENT, MANAGE_PACKAGES). */
    isSuperAdminClient?: boolean;
}

interface ClientRolesApiResponse {
    message: string;
    statusCode: number;
    data: ClientRolesResponse;
}

export async function getClientRoles(clientId: number): Promise<ClientRolesResponse | null> {
    try {
        const response = await apiClient.get<ClientRolesApiResponse | ClientRolesResponse>(`/clients/${clientId}/roles`);
        const body = response as ClientRolesApiResponse & ClientRolesResponse;
        // Support both wrapped { data: { roles, allPermissions } } and unwrapped { roles, allPermissions }
        const data = body?.data ?? body;
        if (data && Array.isArray(data.roles) && Array.isArray(data.allPermissions)) {
            return {
                roles: data.roles,
                allPermissions: data.allPermissions,
                isSuperAdminClient: !!data.isSuperAdminClient,
            };
        }
        return null;
    } catch {
        return null;
    }
}

export async function updateRolePermissions(
    clientId: number,
    roleId: number,
    permissionIds: number[]
): Promise<boolean> {
    try {
        await apiClient.patch<{ message: string; statusCode: number; data: { roleId: number; permissionIds: number[] } }>(
            `/clients/${clientId}/roles/${roleId}`,
            { permissionIds }
        );
        return true;
    } catch {
        return false;
    }
}

export interface CreateRolePayload {
    roleName: string;
    description?: string | null;
    permissionIds?: number[];
}

interface CreateRoleApiResponse {
    message: string;
    statusCode: number;
    data: ClientRoleItem;
}

export async function createClientRole(
    clientId: number,
    payload: CreateRolePayload
): Promise<ClientRoleItem | null> {
    try {
        const response = await apiClient.post<CreateRoleApiResponse>(`/clients/${clientId}/roles`, {
            roleName: payload.roleName.trim(),
            description: payload.description ?? null,
            permissionIds: payload.permissionIds ?? [],
        });
        const body = response as CreateRoleApiResponse;
        return body?.data ?? null;
    } catch {
        return null;
    }
}

// --- Client departments (Departments tab) ---

export interface ClientDepartmentItem {
    departmentId: number;
    clientId: number;
    departmentName: string;
    description: string | null;
    isActive: boolean;
}

export async function getClientDepartments(clientId: number): Promise<ClientDepartmentItem[] | null> {
    try {
        const response = await apiClient.get<{ message: string; statusCode: number; data: ClientDepartmentItem[] }>(
            `/clients/${clientId}/departments`
        );
        const data = (response as any)?.data ?? response;
        return Array.isArray(data) ? data : null;
    } catch {
        return null;
    }
}

export async function createClientDepartment(
    clientId: number,
    payload: { departmentName: string; description?: string | null }
): Promise<ClientDepartmentItem | null> {
    try {
        const response = await apiClient.post<{ message: string; statusCode: number; data: ClientDepartmentItem }>(
            `/clients/${clientId}/departments`,
            {
                departmentName: payload.departmentName.trim(),
                description: payload.description ?? null,
            }
        );
        const data = (response as any)?.data ?? response;
        return data ?? null;
    } catch {
        return null;
    }
}

export async function updateClientDepartment(
    clientId: number,
    departmentId: number,
    payload: { departmentName?: string; description?: string | null }
): Promise<ClientDepartmentItem | null> {
    try {
        const response = await apiClient.patch<{ message: string; statusCode: number; data: ClientDepartmentItem }>(
            `/clients/${clientId}/departments/${departmentId}`,
            payload
        );
        const data = (response as any)?.data ?? response;
        return data ?? null;
    } catch {
        return null;
    }
}

// --- Client users (Users tab) ---

export interface ClientUserItem {
    userId: number;
    username: string;
    email: string;
    isActive: boolean;
    roleIds: number[];
}

export async function getClientUsers(clientId: number): Promise<ClientUserItem[] | null> {
    try {
        const response = await apiClient.get<{ message: string; statusCode: number; data: ClientUserItem[] }>(
            `/clients/${clientId}/users`
        );
        const data = (response as any)?.data ?? response;
        return Array.isArray(data) ? data : null;
    } catch {
        return null;
    }
}

export async function createClientUser(
    clientId: number,
    payload: { username: string; email: string; password: string; roleIds?: number[] }
): Promise<ClientUserItem | null> {
    try {
        const response = await apiClient.post<{ message: string; statusCode: number; data: ClientUserItem }>(
            `/clients/${clientId}/users`,
            {
                username: payload.username.trim(),
                email: payload.email.trim(),
                password: payload.password,
                roleIds: payload.roleIds ?? [],
            }
        );
        const data = (response as any)?.data ?? response;
        return data ?? null;
    } catch {
        return null;
    }
}

export async function updateClientUser(
    clientId: number,
    userId: number,
    payload: { roleIds?: number[]; isActive?: boolean; newPassword?: string }
): Promise<ClientUserItem | null> {
    try {
        const response = await apiClient.patch<{ message: string; statusCode: number; data: ClientUserItem }>(
            `/clients/${clientId}/users/${userId}`,
            payload
        );
        const data = (response as any)?.data ?? response;
        return data ?? null;
    } catch {
        return null;
    }
}

// --- Client Settings (Phase 14) ---

export type ClientSettingsMap = Record<string, string>;

export async function getClientSettings(clientId: number): Promise<ClientSettingsMap | null> {
    try {
        const response = await apiClient.get<{ message: string; statusCode: number; data: ClientSettingsMap }>(
            `/clients/${clientId}/settings`
        );
        return (response as any)?.data ?? response ?? null;
    } catch {
        return null;
    }
}

export async function patchClientSettings(
    clientId: number,
    updates: Record<string, string>
): Promise<ClientSettingsMap | null> {
    try {
        const response = await apiClient.patch<{ message: string; statusCode: number; data: ClientSettingsMap }>(
            `/clients/${clientId}/settings`,
            updates
        );
        return (response as any)?.data ?? response ?? null;
    } catch {
        return null;
    }
}

export interface SmsConfigAdmin {
    configId: number;
    clientId: number;
    providerName: string;
    apiKey: string;
    apiSecret: string | null;
    senderId: string | null;
    templateId: string | null;
    otpTemplateId: string | null;
    shortUrlEnabled: boolean;
    shortUrlProvider: string | null;
    shortUrlApiKey: string | null;
    shortUrlExpiryDays: number | null;
    realTimeResponse: boolean;
}

export async function getClientSmsConfig(clientId: number): Promise<SmsConfigAdmin | null> {
    try {
        const response = await apiClient.get<{ message: string; statusCode: number; data: SmsConfigAdmin | null }>(
            `/clients/${clientId}/sms-config`
        );
        const res = response as { data?: SmsConfigAdmin | null };
        return res?.data !== undefined ? res.data : (response as unknown as SmsConfigAdmin | null) ?? null;
    } catch {
        return null;
    }
}

export async function putClientSmsConfig(
    clientId: number,
    data: Partial<SmsConfigAdmin> & { providerName: string; apiKey: string }
): Promise<SmsConfigAdmin | null> {
    try {
        const response = await apiClient.put<{ message: string; statusCode: number; data: SmsConfigAdmin | null }>(
            `/clients/${clientId}/sms-config`,
            data
        );
        const res = response as { data?: SmsConfigAdmin | null };
        return res?.data !== undefined ? res.data : (response as unknown as SmsConfigAdmin | null) ?? null;
    } catch {
        return null;
    }
}

export interface WhatsAppConfigAdmin {
    configId: number;
    clientId: number;
    providerName: string;
    apiKey: string;
    apiSecret: string | null;
    phoneNumberId: string | null;
    businessAccountId: string | null;
    templateId: string | null;
}

export async function getClientWhatsAppConfig(clientId: number): Promise<WhatsAppConfigAdmin | null> {
    try {
        const response = await apiClient.get<{ message: string; statusCode: number; data: WhatsAppConfigAdmin | null }>(
            `/clients/${clientId}/whatsapp-config`
        );
        const res = response as { data?: WhatsAppConfigAdmin | null };
        return res?.data !== undefined ? res.data : (response as unknown as WhatsAppConfigAdmin | null) ?? null;
    } catch {
        return null;
    }
}

export async function putClientWhatsAppConfig(
    clientId: number,
    data: Partial<WhatsAppConfigAdmin> & { providerName: string; apiKey: string }
): Promise<WhatsAppConfigAdmin | null> {
    try {
        const response = await apiClient.put<{ message: string; statusCode: number; data: WhatsAppConfigAdmin | null }>(
            `/clients/${clientId}/whatsapp-config`,
            data
        );
        const res = response as { data?: WhatsAppConfigAdmin | null };
        return res?.data !== undefined ? res.data : (response as unknown as WhatsAppConfigAdmin | null) ?? null;
    } catch {
        return null;
    }
}

export async function copyProviderConfig(
    clientId: number,
    sourceClientId: number,
    configType: 'sms' | 'whatsapp'
): Promise<{ sms?: SmsConfigAdmin | null; whatsapp?: WhatsAppConfigAdmin | null } | null> {
    try {
        const response = await apiClient.post<{
            message: string;
            statusCode: number;
            data: SmsConfigAdmin | WhatsAppConfigAdmin | null;
        }>(`/clients/${clientId}/copy-provider-config`, { sourceClientId, configType });
        const data = (response as any)?.data ?? response;
        if (configType === 'sms') return { sms: data as SmsConfigAdmin };
        return { whatsapp: data as WhatsAppConfigAdmin };
    } catch {
        return null;
    }
}

/** Privigo DPDPA badge certificate (admin). */
export interface ClientCertificate {
    certId: string;
    clientId: number;
    organisationName: string;
    tier: 'ready' | 'compliant';
    tierLabel: string;
    status: 'active' | 'expired' | 'revoked';
    issuedAt: string;
    validUntil: string;
    scopeText: string;
    dpPortalUrl: string | null;
    verifyUrl: string;
    embedHtml: string;
}

export interface ClientCertificateResponse {
    clientId: number;
    clientName: string;
    subdomain: string;
    certificate: ClientCertificate | null;
}

export async function getClientCertificate(clientId: number): Promise<ClientCertificateResponse | null> {
    try {
        const response = await apiClient.get<{
            message: string;
            statusCode: number;
            data: ClientCertificateResponse;
        }>(`/clients/${clientId}/certificate`);
        const body = response as { data?: ClientCertificateResponse };
        return body?.data ?? (response as unknown as ClientCertificateResponse) ?? null;
    } catch {
        return null;
    }
}

export async function issueClientCertificate(
    clientId: number,
    payload: {
        tier?: 'ready' | 'compliant';
        organisationName?: string;
        validUntil?: string;
        dpPortalUrl?: string | null;
    } = {}
): Promise<ClientCertificate | null> {
    try {
        const response = await apiClient.post<{
            message: string;
            statusCode: number;
            data: { certificate: ClientCertificate };
        }>(`/clients/${clientId}/certificate`, payload);
        const body = response as { data?: { certificate: ClientCertificate } };
        return body?.data?.certificate ?? null;
    } catch {
        return null;
    }
}

export async function revokeClientCertificate(clientId: number): Promise<{ revoked: number } | null> {
    try {
        const response = await apiClient.post<{
            message: string;
            statusCode: number;
            data: { revoked: number };
        }>(`/clients/${clientId}/certificate/revoke`, {});
        const body = response as { data?: { revoked: number } };
        return body?.data ?? null;
    } catch {
        return null;
    }
}
